<div class="main-container-finance">
  <div class="breadcrumb">
    <a (click)="backToDashboard()" class="crumb crumb-link">Finance Dashboard</a>
    <span class="crumb-sep">/</span>
    <a (click)="backToDetails()" class="crumb crumb-link">Finance Details</a>
    <span class="crumb-sep">/</span>
    <span class="crumb crumb-current">Edit Project</span>
  </div>

  <div *ngIf="loading" class="state-block">Loading…</div>
  <div *ngIf="errorMessage && !loading" class="state-block state-error">{{ errorMessage }}</div>

  <div *ngIf="!loading && !errorMessage" class="edit-layout">

    <!-- Left: Edit form -->
    <mat-card class="content-card edit-form-card">
      <div class="resource-header">
        <div class="resource-identity">
          <mat-icon class="resource-icon">person</mat-icon>
          <div class="resource-meta">
            <span class="resource-name">{{ resourceName || 'Resource' }}</span>
            <span class="resource-id" *ngIf="employeeId">Employee ID: {{ employeeId }}</span>
          </div>
        </div>
      </div>

      <mat-tab-group
        [(selectedIndex)]="activeMainTab"
        class="main-tabs"
        (selectedTabChange)="onMainTabChange($event.index)"
      >

        <!-- Current Project tab (active) -->
        <mat-tab label="Current Project">
          <form [formGroup]="editForm" class="edit-form-body">
            <ng-container formArrayName="projects">
              <div
                *ngFor="let group of projectsArray.controls; let i = index"
                [formGroupName]="i"
                class="project-section"
                [class.is-collapsed]="!isExpanded(i)"
              >
                <button
                  type="button"
                  class="project-toggle"
                  (click)="toggleSection(i)"
                  [attr.aria-expanded]="isExpanded(i)"
                  [attr.aria-label]="(isExpanded(i) ? 'Collapse ' : 'Expand ') + sectionLabel(i)"
                >
                  <span class="project-label">{{ sectionLabel(i) }}</span>
                  <span class="project-summary" *ngIf="!isExpanded(i)">
                    {{ group.value.projectName || '—' }}
                    <span *ngIf="group.value.projectIdCode" class="project-summary-id">{{ group.value.projectIdCode }}</span>
                  </span>
                  <mat-icon class="chevron" [class.rotated]="isExpanded(i)">expand_more</mat-icon>
                </button>

                <div class="form-grid" *ngIf="isExpanded(i)">
                  <!-- Hiring Status — Material auto-renders the * because
                       the form control has Validators.required, so the
                       label text itself drops the manual asterisk. -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Hiring Status</mat-label>
                    <mat-select formControlName="hiringStatus">
                      <mat-option *ngFor="let o of hiringStatusOptions" [value]="o.code">
                        {{ o.label }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <!-- Project Name & ID — picker. Selecting a different
                       project auto-refreshes the Supporting LOB portion of
                       the read-only Leader & Supporting LOB display below. -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Project Name &amp; ID</mat-label>
                    <mat-select
                      formControlName="projectId"
                      required
                      (selectionChange)="onProjectChange(i, $event.value)"
                    >
                      <mat-option *ngFor="let p of projectOptions" [value]="p.id">
                        {{ p.projectName }}<span class="opt-id" *ngIf="p.projectIdCode"> | {{ p.projectIdCode }}</span>
                      </mat-option>
                      <mat-option *ngIf="projectOptions.length === 0" disabled>
                        Loading projects…
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <!-- Leader & Supporting LOB — read-only, composed from
                       the assignment's dntIndiaLeader + the selected
                       project's supportingLob. -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Leader &amp; Supporting LOB</mat-label>
                    <input matInput readonly [value]="composedLeader(group.value)" />
                  </mat-form-field>

                  <!-- Allocation — % glyph rendered via CSS ::after on the
                       form-field infix so it visually butts against the
                       right-aligned number. matSuffix was leaving a
                       gutter that looked disconnected. -->
                  <mat-form-field appearance="outline" floatLabel="always" class="allocation-field">
                    <mat-label>Allocation</mat-label>
                    <input matInput type="number" min="0" max="100" formControlName="allocationPct" />
                  </mat-form-field>

                  <!-- Start Date — read-only per Figma. Material datepicker
                       gives the calendar icon visual; the [disabled] state
                       on the picker prevents the popup from opening. -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Start Date</mat-label>
                    <input
                      matInput
                      [matDatepicker]="startPicker"
                      formControlName="billingStartDate"
                      readonly
                    />
                    <mat-datepicker-toggle matIconSuffix [for]="startPicker" disabled></mat-datepicker-toggle>
                    <mat-datepicker #startPicker></mat-datepicker>
                  </mat-form-field>

                  <!-- End Date — editable per Figma. Material datepicker
                       opens a popup calendar on icon click. -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>End Date</mat-label>
                    <input
                      matInput
                      [matDatepicker]="endPicker"
                      formControlName="billingEndDate"
                    />
                    <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                    <mat-datepicker #endPicker></mat-datepicker>
                  </mat-form-field>

                  <!-- Tier Model — editable; drives the monthly Total formula via
                       finance_tier_rate (base_rate + office_exp) lookup. Picking
                       a tier auto-fills Rate Card (Base Rate) from the master. -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Tier Model</mat-label>
                    <mat-select
                      formControlName="tierModel"
                      (selectionChange)="onTierModelChange($any(group), $event.value)"
                    >
                      <mat-option [value]="''">—</mat-option>
                      <mat-option *ngFor="let t of tierOptions" [value]="t">{{ t }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <!-- Rate Card (Base Rate) — auto-fills when Tier Model is
                       picked; operator can still override before saving. -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Rate Card</mat-label>
                    <span matPrefix>$&nbsp;</span>
                    <input matInput type="number" step="0.01" formControlName="baseRate" />
                  </mat-form-field>

                  <!-- Bill to FU — read-only per Figma -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Bill to FU</mat-label>
                    <input matInput formControlName="billToFu" readonly />
                  </mat-form-field>

                  <!-- Bill to MO — read-only per Figma -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Bill to MO</mat-label>
                    <input matInput formControlName="billToMo" readonly />
                  </mat-form-field>

                  <!-- Bill to BU — read-only per Figma (maps to glBusinessUnit on rate card) -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Bill to BU</mat-label>
                    <input matInput formControlName="billToBu" readonly />
                  </mat-form-field>

                  <!-- Vendor Rate Card — editable, $ prefix to match Rate Card -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Vendor Rate Card</mat-label>
                    <span matPrefix>$&nbsp;</span>
                    <input matInput type="number" step="0.01" formControlName="vendorRateCard" />
                  </mat-form-field>

                  <!-- Laptop Issued — canonical dropdown -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Laptop Issued</mat-label>
                    <mat-select formControlName="laptopIssued">
                      <mat-option [value]="''">—</mat-option>
                      <mat-option *ngFor="let opt of laptopIssuedOptions" [value]="opt">{{ opt }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <!-- GL Account — derived from CAPEX/OPEX, read-only -->
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>GL Account</mat-label>
                    <input matInput formControlName="glAccount" readonly />
                  </mat-form-field>
                </div>

              </div>

              <div *ngIf="projectsArray.length === 0" class="state-block empty-block">
                <mat-icon class="empty-icon">edit_off</mat-icon>
                <p class="empty-title">No assignments to edit</p>
                <p class="empty-hint">
                  This resource has no active project assignments. Add one
                  via the <strong>New Project</strong> tab when it ships.
                </p>
              </div>
            </ng-container>
          </form>

          <div class="form-actions" *ngIf="projectsArray.length > 0">
            <button mat-stroked-button type="button" (click)="cancel()" [disabled]="saving">Cancel</button>
            <button
              mat-flat-button
              type="button"
              class="save-btn"
              (click)="save()"
              [disabled]="saving || editForm.invalid || editForm.pristine"
            >
              {{ saving ? 'Updating…' : 'Update' }}
            </button>
          </div>
        </mat-tab>

        <!-- New Project tab — add a fresh assignment for this resource -->
        <mat-tab label="New Project">
          <form [formGroup]="newProjectForm" class="edit-form-body">
            <div class="project-section">
              <div class="form-grid">
                <!-- Hiring Status -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Hiring Status</mat-label>
                  <mat-select formControlName="hiringStatus">
                    <mat-option *ngFor="let o of hiringStatusOptions" [value]="o.code">
                      {{ o.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>

                <!-- Project Name & ID picker -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Project Name &amp; ID</mat-label>
                  <mat-select
                    formControlName="projectId"
                    required
                    (selectionChange)="onNewProjectChange($event.value)"
                  >
                    <mat-option *ngFor="let p of projectOptions" [value]="p.id">
                      {{ p.projectName }}<span class="opt-id" *ngIf="p.projectIdCode"> | {{ p.projectIdCode }}</span>
                    </mat-option>
                    <mat-option *ngIf="projectOptions.length === 0" disabled>
                      Loading projects…
                    </mat-option>
                  </mat-select>
                </mat-form-field>

                <!-- CAPEX/OPEX — required by the DB constraint on the
                     assignment row. Reference data drives the options. -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>CAPEX/OPEX</mat-label>
                  <mat-select formControlName="capexOpex">
                    <mat-option *ngFor="let o of capexOpexOptions" [value]="o.code">
                      {{ o.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>

                <!-- D&T India Leader — editable on new (assignment-scoped) -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>D&amp;T India Leader</mat-label>
                  <input matInput formControlName="dntIndiaLeader" />
                </mat-form-field>

                <!-- Supporting LOB — read-only, auto-fills from picker -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Supporting LOB</mat-label>
                  <input matInput readonly formControlName="supportingLob" />
                </mat-form-field>

                <!-- Allocation -->
                <mat-form-field appearance="outline" floatLabel="always" class="allocation-field">
                  <mat-label>Allocation</mat-label>
                  <input matInput type="number" min="0" max="100" formControlName="allocationPct" />
                </mat-form-field>

                <!-- Start Date — editable -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Start Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="newStartPicker"
                    formControlName="billingStartDate"
                  />
                  <mat-datepicker-toggle matIconSuffix [for]="newStartPicker"></mat-datepicker-toggle>
                  <mat-datepicker #newStartPicker></mat-datepicker>
                </mat-form-field>

                <!-- End Date — editable -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>End Date</mat-label>
                  <input
                    matInput
                    [matDatepicker]="newEndPicker"
                    formControlName="billingEndDate"
                  />
                  <mat-datepicker-toggle matIconSuffix [for]="newEndPicker"></mat-datepicker-toggle>
                  <mat-datepicker #newEndPicker></mat-datepicker>
                </mat-form-field>

                <!-- Tier Model — drives the monthly Total formula via
                     finance_tier_rate. Picking a tier auto-fills Rate Card
                     (Base Rate) from the master; operator can override. -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Tier Model</mat-label>
                  <mat-select
                    formControlName="tierModel"
                    (selectionChange)="onTierModelChange(newProjectForm, $event.value)"
                  >
                    <mat-option [value]="''">—</mat-option>
                    <mat-option *ngFor="let t of tierOptions" [value]="t">{{ t }}</mat-option>
                  </mat-select>
                </mat-form-field>

                <!-- Rate Card (Base Rate) — auto-fills from the picked tier's
                     master rate; operator can still override before saving. -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Rate Card</mat-label>
                  <span matPrefix>$&nbsp;</span>
                  <input matInput type="number" step="0.01" formControlName="baseRate" />
                </mat-form-field>

                <!-- Bill to FU -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Bill to FU</mat-label>
                  <input matInput formControlName="billToFu" />
                </mat-form-field>

                <!-- Bill to MO -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Bill to MO</mat-label>
                  <input matInput formControlName="billToMo" />
                </mat-form-field>

                <!-- Bill to BU (maps to glBusinessUnit on rate card) -->
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Bill to BU</mat-label>
                  <input matInput formControlName="billToBu" />
                </mat-form-field>

              </div>

            </div>
          </form>

          <div class="form-actions">
            <button mat-stroked-button type="button" (click)="cancel()" [disabled]="addingProject">Cancel</button>
            <button
              mat-flat-button
              type="button"
              class="save-btn"
              (click)="addProject()"
              [disabled]="addingProject || newProjectForm.invalid"
            >
              {{ addingProject ? 'Adding…' : 'Add Project' }}
            </button>
          </div>
        </mat-tab>

        <!-- One Time tab — list existing entries + add a new one. The
             entity is immutable (no edit / delete) so this surface is
             append-only; operators post a correcting entry to fix
             a mistake. -->
        <mat-tab label="One Time">
          <div class="edit-form-body one-time-body">

            <!-- Existing entries — read-only list -->
            <section class="one-time-list-section">
              <h3 class="section-subtitle">Posted one-time entries</h3>

              <div *ngIf="oneTimeLoading" class="state-block">Loading…</div>

              <div *ngIf="!oneTimeLoading && oneTimeEntries.length === 0" class="state-block empty-block">
                <mat-icon class="empty-icon">event_note</mat-icon>
                <p class="empty-title">No one-time entries yet</p>
                <p class="empty-hint">Post the resource's first one-off adjustment using the form below.</p>
              </div>

              <table
                *ngIf="!oneTimeLoading && oneTimeEntries.length > 0"
                mat-table
                [dataSource]="oneTimeEntries"
                class="one-time-table"
              >
                <ng-container matColumnDef="period">
                  <th mat-header-cell *matHeaderCellDef>Period</th>
                  <td mat-cell *matCellDef="let row">
                    {{ monthLabel(row.targetMonth) }} {{ row.targetYear }}
                  </td>
                </ng-container>

                <ng-container matColumnDef="amount">
                  <th mat-header-cell *matHeaderCellDef>Amount</th>
                  <td mat-cell *matCellDef="let row">{{ formatCurrency(row.amount) }}</td>
                </ng-container>

                <ng-container matColumnDef="comments">
                  <th mat-header-cell *matHeaderCellDef>Comments</th>
                  <td mat-cell *matCellDef="let row" [matTooltip]="row.comments">{{ row.comments }}</td>
                </ng-container>

                <ng-container matColumnDef="createdBy">
                  <th mat-header-cell *matHeaderCellDef>Posted By</th>
                  <td mat-cell *matCellDef="let row">{{ row.createdBy }}</td>
                </ng-container>

                <ng-container matColumnDef="createdAt">
                  <th mat-header-cell *matHeaderCellDef>Posted At</th>
                  <td mat-cell *matCellDef="let row">{{ formatTimestamp(row.createdAt) }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="oneTimeColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: oneTimeColumns"></tr>
              </table>
            </section>

            <!-- Add form -->
            <section class="one-time-add-section">
              <h3 class="section-subtitle">Add a one-time entry</h3>

              <form [formGroup]="oneTimeForm" class="project-section">
                <div class="form-grid">
                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Year</mat-label>
                    <input matInput type="number" min="2000" max="2100" formControlName="targetYear" />
                  </mat-form-field>

                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Month</mat-label>
                    <mat-select formControlName="targetMonth">
                      <mat-option *ngFor="let m of monthOptions" [value]="m.value">{{ m.label }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Amount</mat-label>
                    <span matPrefix>$&nbsp;</span>
                    <input matInput type="number" step="0.01" formControlName="amount" />
                  </mat-form-field>

                  <mat-form-field appearance="outline" floatLabel="always" class="comments-field">
                    <mat-label>Comments</mat-label>
                    <textarea matInput rows="2" formControlName="comments" placeholder="What this entry covers"></textarea>
                  </mat-form-field>
                </div>
              </form>
            </section>

          </div>

          <div class="form-actions">
            <button mat-stroked-button type="button" (click)="cancel()" [disabled]="addingOneTime">Cancel</button>
            <button
              mat-flat-button
              type="button"
              class="save-btn"
              (click)="addOneTimeEntry()"
              [disabled]="addingOneTime || oneTimeForm.invalid"
            >
              {{ addingOneTime ? 'Adding…' : 'Add One Time Entry' }}
            </button>
          </div>
        </mat-tab>

      </mat-tab-group>
    </mat-card>

    <!-- Right: history + activities side panel -->
    <mat-card class="content-card side-panel-card">
      <mat-tab-group class="side-tabs">

        <mat-tab label="Project History">
          <div class="side-panel-body">
            <div *ngIf="rightPanelLoading" class="state-block">Loading…</div>
            <div *ngIf="!rightPanelLoading && projectHistory.length === 0" class="state-block empty-block">
              <mat-icon class="empty-icon">history_toggle_off</mat-icon>
              <p class="empty-title">No history yet</p>
              <p class="empty-hint">Edits to this resource will appear here as they happen.</p>
            </div>
            <div *ngFor="let entry of projectHistory" class="history-entry">
              <div class="entry-header">{{ formatHistoryHeader(entry) }}</div>
              <div class="entry-grid">
                <div class="kv">
                  <span class="k">Project Name &amp; ID</span>
                  <span class="v">
                    {{ entry.projectName || '—' }}
                    <span *ngIf="entry.projectIdCode" class="kv-sub">{{ entry.projectIdCode }}</span>
                  </span>
                </div>
                <div class="kv">
                  <span class="k">Leader</span>
                  <span class="v">{{ snapshotValue(entry.snapshot['D&T India Leader']) }}</span>
                </div>
                <div class="kv">
                  <span class="k">Supporting LOB</span>
                  <span class="v">{{ snapshotValue(entry.snapshot['Supporting LOB']) }}</span>
                </div>
                <div class="kv">
                  <span class="k">Allocation</span>
                  <span class="v">{{ snapshotValue(entry.snapshot['Allocation']) }}</span>
                </div>
                <div class="kv">
                  <span class="k">Start Date</span>
                  <span class="v">{{ snapshotValue(entry.snapshot['Start Date']) }}</span>
                </div>
                <div class="kv">
                  <span class="k">End Date</span>
                  <span class="v">{{ snapshotValue(entry.snapshot['End Date']) }}</span>
                </div>
              </div>
            </div>
          </div>
        </mat-tab>

        <mat-tab label="Recent Activities">
          <div class="side-panel-body">
            <div *ngIf="rightPanelLoading" class="state-block">Loading…</div>
            <div *ngIf="!rightPanelLoading && recentActivities.length === 0" class="state-block empty-block">
              <mat-icon class="empty-icon">person_off</mat-icon>
              <p class="empty-title">No activity yet</p>
              <p class="empty-hint">Your edits to this resource will appear here.</p>
            </div>

            <!-- One card per Recent Activity. Each card shows the
                 From/To diff matching the Figma's comparison layout —
                 only fields that actually changed appear in the grid;
                 Project Name & ID rides along on every entry as context. -->
            <div *ngFor="let entry of recentActivities" class="history-entry">
              <div class="entry-header">{{ formatRecentHeader(entry) }}</div>

              <div class="entry-project" *ngIf="entry.projectName || entry.projectIdCode">
                <span class="project-name">{{ entry.projectName || '—' }}</span>
                <span class="project-id" *ngIf="entry.projectIdCode">{{ entry.projectIdCode }}</span>
              </div>

              <!-- CREATE event (no prior snapshot) — show "Initial" label
                   and the To-side values inline. -->
              <ng-container *ngIf="!entry.changes || entry.changes.length === 0">
                <div class="diff-empty">
                  <ng-container *ngIf="entry.changeType === 'CREATE'">
                    Created with no prior state to compare.
                  </ng-container>
                  <ng-container *ngIf="entry.changeType !== 'CREATE'">
                    No tracked field changed.
                  </ng-container>
                </div>
              </ng-container>

              <!-- UPDATE / DELETE with diffs — From column then To column. -->
              <div *ngIf="entry.changes && entry.changes.length > 0" class="diff-grid">
                <div class="diff-col">
                  <div class="diff-label">From</div>
                  <ul class="diff-list">
                    <li *ngFor="let c of entry.changes">
                      <span class="diff-field">{{ c.field }}</span>
                      <span class="diff-val">{{ snapshotValue(c.from) }}</span>
                    </li>
                  </ul>
                </div>
                <div class="diff-col">
                  <div class="diff-label">To</div>
                  <ul class="diff-list">
                    <li *ngFor="let c of entry.changes">
                      <span class="diff-field">{{ c.field }}</span>
                      <span class="diff-val">{{ snapshotValue(c.to) }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </mat-tab>

      </mat-tab-group>
    </mat-card>

  </div>
</div>
